// blockNameSuggestions — I-102: föreslå block-namn baserat på
// connector-kontext och block-typ. Används i PropertiesPanel när user
// börjar skriva ett block-namn; suggestion-chips fyller fältet med ett
// klick.
//
// Pure: inga side-effekter, ingen DOM. `suggestBlockNames(block,
// connectors?)` returnerar 3-5 förslag i prio-ordning.

import type { WorkflowBlock, BlockType } from '@/types/workflow';
import type { ConnectorKind } from '@/lib/connectorsRegistry';

export interface SuggestionInput {
  block: Pick<WorkflowBlock, 'type' | 'config'>;
  /** Connector-kontext — hämtas från block.config.connectorKind om set. */
  connectorKind?: ConnectorKind;
  /** Valfri use-case-hint (t.ex. från block.config.useCase eller tab-namn). */
  context?: string;
}

type SuggestionMap = Record<string, readonly string[]>;

/**
 * Per-type default-förslag. Matchar våra 12 built-in BlockTypes.
 * Ordered mest→minst specifika.
 */
const TYPE_SUGGESTIONS: Partial<Record<BlockType, readonly string[]>> = {
  general: ['Steg', 'Uppgift', 'Utför åtgärd'],
  condition: ['Beslut', 'Kontroll', 'Villkor', 'Är X uppfyllt?'],
  'send-email': ['Skicka notis', 'E-posta beslut', 'Mail till ansvarig'],
  'create-user': ['Skapa användare', 'Onboard user'],
  'disable-mailbox': ['Stäng brevlåda', 'Offboard user'],
  'delete-m365': ['Ta bort M365-konto', 'Offboard user'],
  webhook: ['Webhook-trigger', 'Ta emot event', 'Inkommande webhook'],
  'add-to-group': ['Lägg till i grupp', 'Tilldela roll', 'Gruppera user'],
};

/**
 * Per-connector-kind-förslag — läggs ovanpå type-förslagen när connectorn
 * är känd. Typiska integration-use-cases.
 */
const CONNECTOR_SUGGESTIONS: SuggestionMap = {
  'rest-http': ['Hämta data via API', 'POST till API', 'Uppdatera fält'],
  'graphql': ['GraphQL-query', 'Mutation', 'Hämta dataset'],
  'postgres': ['Läs från databas', 'Uppdatera rad', 'Spara i DB'],
  'mysql': ['Läs från MySQL', 'Uppdatera rad', 'Spara i DB'],
  'mongodb': ['Hämta dokument', 'Uppdatera dokument', 'Spara i Mongo'],
  'webhook': ['Webhook-mottagare', 'Ta emot event'],
  'oauth-generic': ['Autentisera user', 'OAuth-flow'],
  's3': ['Ladda upp fil', 'Hämta fil', 'Lista bucket'],
  'smtp': ['Skicka mail', 'E-posta notis'],
  'imap': ['Läs inbox', 'Hämta e-post'],
};

/** Maxlängd på förslag. */
const MAX_SUGGESTIONS = 5;

export function suggestBlockNames(input: SuggestionInput): string[] {
  const { block, connectorKind, context } = input;
  const suggestions: string[] = [];

  // 1) Connector-specifika förslag först om connector-kind är känd.
  if (connectorKind && CONNECTOR_SUGGESTIONS[connectorKind]) {
    suggestions.push(...CONNECTOR_SUGGESTIONS[connectorKind]);
  }

  // 2) Type-specifika förslag.
  const typeDefaults = TYPE_SUGGESTIONS[block.type];
  if (typeDefaults) {
    suggestions.push(...typeDefaults);
  }

  // 3) Context-baserat förslag — om context-strängen innehåller
  // domänord bygger vi ett förslag från det. Tolererar tomma context:er.
  if (context) {
    const trimmed = context.trim();
    if (trimmed.length > 0 && trimmed.length < 40) {
      suggestions.push(trimmed);
    }
  }

  // De-dupe + clamp.
  const seen = new Set<string>();
  const deduped: string[] = [];
  for (const s of suggestions) {
    const key = s.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      deduped.push(s);
    }
    if (deduped.length >= MAX_SUGGESTIONS) break;
  }
  return deduped;
}
